import React, { useState } from 'react';
import {
    Modal,
    Box,
    Typography,
    Grid,
    Tooltip,
    IconButton,
    Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { formatDateMX } from '../../../helpers/hours';
import { money } from '../../../helpers/utils';
import {
    reasonMapping,
    referenceMapping,
    trafficLightMapping,
} from '../../utils/lists';

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '80vw',
    height: '80vh',
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
    borderRadius: 4,
    overflowY: 'auto',
};

const mapLabel = (value, mapping) => {
    const item = mapping.find(item => item.value === value);
    return item ? item.label : value || 'N/A';
};

const CopyableField = ({ label, value, onCopy, disableCopy }) => (
    <Grid item xs={6} display="flex" alignItems="center">
        <Typography variant="subtitle1" gutterBottom sx={{ flex: 1 }}>
            <strong>{label}:</strong>
        </Typography>
        <Typography
            variant="subtitle1"
            gutterBottom
            sx={{ textAlign: 'right', flex: 1 }}
        >
            {value || '-'}
        </Typography>
        {!disableCopy && value && (
            <Tooltip title="Copiar" placement="top" arrow>
                <IconButton
                    onClick={() => onCopy(value)}
                    size="small"
                    aria-label={`Copiar ${label}`}
                >
                    <ContentCopyIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        )}
    </Grid>
);

export const SummaryModal = ({ open, handleClose, form }) => {
    const [showObservations, setShowObservations] = useState(false);

    const toggleObservations = () => setShowObservations(!showObservations);

    return (
        <Modal open={open} onClose={handleClose} aria-labelledby="modal-title">
            <Box sx={style}>
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                >
                    <Typography
                        id="modal-title"
                        variant="h6"
                        sx={{ fontWeight: 'bold', marginBottom: '20px' }}
                    >
                        Resumen de la Prospección
                    </Typography>
                    <IconButton
                        onClick={handleClose}
                        aria-label="Cerrar"
                        size="small"
                        sx={{ marginBottom: '20px' }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Grid container spacing={2}>
                    <Grid item xs={12} container spacing={2}>
                        <CopyableField
                            label="Referencia"
                            value={mapLabel(form.reference, referenceMapping)}
                            onCopy={text => navigator.clipboard.writeText(text)}
                        />
                        <CopyableField
                            label="Prospecto"
                            value={form.clientName || 'N/A'}
                            onCopy={text => navigator.clipboard.writeText(text)}
                        />
                        <CopyableField
                            label="Semáforo Contacto"
                            value={mapLabel(
                                form.trafficLightContact,
                                trafficLightMapping
                            )}
                            onCopy={text => navigator.clipboard.writeText(text)}
                        />
                        <CopyableField
                            label="Fecha Contacto"
                            value={formatDateMX(form.date1)}
                            onCopy={text => navigator.clipboard.writeText(text)}
                        />
                        <CopyableField
                            label="Semáforo Cita"
                            value={mapLabel(
                                form.trafficLightAppointment,
                                trafficLightMapping
                            )}
                            onCopy={text => navigator.clipboard.writeText(text)}
                        />
                        <CopyableField
                            label="Fecha Cita"
                            value={formatDateMX(form.date2)}
                            onCopy={text => navigator.clipboard.writeText(text)}
                        />
                        <CopyableField
                            label="Semáforo Lead"
                            value={mapLabel(
                                form.trafficLightLead,
                                trafficLightMapping
                            )}
                            onCopy={text => navigator.clipboard.writeText(text)}
                        />
                        <CopyableField
                            label="Fecha Lead"
                            value={formatDateMX(form.date3)}
                            onCopy={text => navigator.clipboard.writeText(text)}
                        />
                        <CopyableField
                            label="Semáforo Cotización"
                            value={mapLabel(
                                form.trafficLightQuote,
                                trafficLightMapping
                            )}
                            onCopy={text => navigator.clipboard.writeText(text)}
                        />
                        <CopyableField
                            label="Fecha Cotización"
                            value={formatDateMX(form.date4)}
                            onCopy={text => navigator.clipboard.writeText(text)}
                        />
                        <CopyableField
                            label="Semáforo Cierre"
                            value={mapLabel(
                                form.trafficLightClosing,
                                trafficLightMapping
                            )}
                            onCopy={text => navigator.clipboard.writeText(text)}
                        />
                        <CopyableField
                            label="Fecha Cierre"
                            value={formatDateMX(form.date5)}
                            onCopy={text => navigator.clipboard.writeText(text)}
                        />
                        <CopyableField
                            label="Valor de Venta"
                            value={
                                form.saleValue
                                    ? money(form.saleValue)
                                    : 'N/A'
                            }
                            onCopy={text =>
                                navigator.clipboard.writeText(
                                    money(form.saleValue)
                                )
                            }
                        />

                        <CopyableField
                            label="Motivo de No Compra"
                            value={mapLabel(
                                form.reasonNoPurchase,
                                reasonMapping
                            )}
                            onCopy={text => navigator.clipboard.writeText(text)}
                        />
                    </Grid>
                    {showObservations && (
                        <Grid item xs={12} sx={{ mt: 2 }}>
                            <Typography variant="subtitle1" gutterBottom>
                                <strong>Observaciones:</strong>
                            </Typography>
                            <Box
                                sx={{
                                    p: 1,
                                    border: '1px solid #e0e0e0',
                                    borderRadius: 1,
                                    bgcolor: '#f9f9f9',
                                }}
                            >
                                <Typography variant="body2">
                                    {form.observations || '-'}
                                </Typography>
                            </Box>
                        </Grid>
                    )}
                </Grid>
                <Button
                    onClick={toggleObservations}
                    sx={{
                        textTransform: 'none',
                        color: '#1976d2',
                        mt: 2,
                        display: 'flex',
                        mx: 'auto',
                    }}
                >
                    {showObservations
                        ? 'Ocultar Observaciones'
                        : 'Mostrar Observaciones'}
                </Button>
                <Box display="flex" justifyContent="flex-end" mt={3}>
                    <Button
                        onClick={handleClose}
                        sx={{ color: 'red', textTransform: 'none' }}
                    >
                        Cerrar
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};
